import { EventType } from 'lib/events';
import { SUBSCRIPTION_BENEFITS_ROUTE } from 'lib/member-experience';
import { ExtraLink, NavItem, NavigationItem } from './top-navigation.types';

// Takes journeyHref as an argument because the per-member destination depends
// on query data unavailable at module scope.
const getJourneyNavItem = (journeyHref: string): NavItem => ({
  title: 'Your Journey',
  text: 'Track your progress and understand the science behind DS-01®.',
  media: {
    src: 'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1785202964/member-experience/table_of_contents/53ef1d9488145ddc711574a2ccf7ec1b6c94e080.png',
    alt: 'Your Journey',
  },
  href: journeyHref,
});

const primaryList: NavItem[] = [
  {
    title: 'InnerCircle',
    text: 'Manage your membership and earn rewards.',
    media: {
      src: 'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1752272149/library/nav/account/innercircle.png',
      alt: 'InnerCircle',
    },
    href: '/account/inner-circle',
  },
  {
    title: 'Refer',
    text: 'Give $25 or Get $25 for every referral.',
    media: {
      src: 'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1752272148/library/nav/account/refer.png',
      alt: 'Refer',
    },
    href: '/account/refer?traffic_source=nav_earn_secondary',
  },
];

// One-time purchasers have nothing to manage under Journey, InnerCircle or
// Refer, so the menu leads with a single card into the DS-01 subscribe page.
const subscribeNavItem: NavItem = {
  title: 'Subscribe to DS-01®',
  text: 'Save 25%',
  textInPill: true,
  textBeforeTitle: true,
  media: {
    src: 'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1755883106/cart/DS-01_80x80.png',
    alt: 'DS-01® Daily Synbiotic jar',
  },
  mediaTile: true,
  href: SUBSCRIPTION_BENEFITS_ROUTE,
};

const subscriptionsLink: ExtraLink = {
  text: 'Subscriptions',
  url: '/account/subscriptions',
};

const accountLinks: ExtraLink[] = [
  subscriptionsLink,
  {
    text: 'Order History',
    url: '/account/orders',
  },
  {
    text: 'Settings',
    url: '/account',
  },
];

const oneTimePurchaserLinks = accountLinks.filter(
  (link) => link !== subscriptionsLink,
);

const baseAccountDropdownContent: NavigationItem = {
  id: 'account',
  label: 'Account',
  subNavLists: [{ heading: '', list: primaryList }],
  extraLinks: {
    heading: 'Account',
    list: accountLinks,
  },
  extraLinkRight: {
    text: 'Sign Out',
    // Figma: 14px title-case, underlined, with the trailing arrow.
    underline: true,
    small: true,
    transform: 'capitalize',
    action: () => {
      window.dispatchEvent(new Event(EventType.LOGOUT));
    },
  },
};

export interface AccountDropdownOptions {
  /** Redesign flag is on for the member: prepend the "Your Journey" card. */
  showJourney: boolean;
  journeyHref: string;
  /** Member has no subscriptions: swap the cards and Subscriptions link for the DS-01 subscribe card. */
  isOneTimePurchaser?: boolean;
}

export const getAccountDropdownContent = ({
  showJourney,
  journeyHref,
  isOneTimePurchaser = false,
}: AccountDropdownOptions): NavigationItem => {
  if (isOneTimePurchaser) {
    return {
      ...baseAccountDropdownContent,
      subNavLists: [{ heading: '', list: [subscribeNavItem] }],
      extraLinks: { heading: 'Account', list: oneTimePurchaserLinks },
    };
  }
  if (showJourney) {
    return {
      ...baseAccountDropdownContent,
      subNavLists: [
        {
          heading: '',
          list: [getJourneyNavItem(journeyHref), ...primaryList],
        },
      ],
    };
  }
  return baseAccountDropdownContent;
};
